import { useEffect, useMemo, useCallback, useState } from 'react';
import type { ApolloError } from 'apollo-client';
import { type SortingDirection, getVariables } from '@atlassian/jira-business-common';
import { useJqlContext } from '@atlassian/jira-business-entity-common';
import { getAGGCompliantData, useJqlPagination } from '@atlassian/jira-business-list-handlers';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useFlagService } from '@atlassian/jira-flags/src/services';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { transformData } from '../../common/utils/transform-data';
import type { CalendarDataSourceItem, CalendarIssue, UnscheduledItem } from '../../types';
import { CALENDAR_ITEM_FIELDS, UNSCHEDULED_ITEMS_JQL } from '../constants';
import type {
	JWMCalendarUnscheduledItems,
	JWMCalendarUnscheduledItemsVariables,
	JWMCalendarUnscheduledItems_jira_list_JiraWorkManagementViewItemConnection_edges_node as ViewItemsNode,
	JWMCalendarUnscheduledItems_jira_list_JiraWorkManagementViewItemConnection_edges as ViewItemsEdges,
} from './__generated_apollo__/JWMCalendarUnscheduledItems';
import { QUERY } from './gql';
import messages from './messages';

const QUERY_NAME = 'unscheduledItemsCalendarViewQuery';
const PAGE_SIZE = 50;

type UnscheduledItemsInput = {
	jqlFilters: string;
	sortDirection: SortingDirection;
	jqlSimpleSearch: string;
};

export const useUnscheduledItems = ({
	sortDirection,
	jqlFilters,
	jqlSimpleSearch,
}: UnscheduledItemsInput) => {
	const [isInitialised, setIsInitialised] = useState(false);
	const { showFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const jqlContext = useJqlContext();
	const cloudId = useCloudId();

	const variables = useMemo(() => {
		const jql = [UNSCHEDULED_ITEMS_JQL];

		if (jqlFilters) {
			jql.push(jqlFilters);
		}

		if (jqlSimpleSearch) {
			jql.push(jqlSimpleSearch);
		}

		return getVariables({
			jqlContext,
			fieldIds: CALENDAR_ITEM_FIELDS,
			sorting: { sortBy: 'created', direction: sortDirection },
			quickFiltersJql: `${jql.join(' AND ')}`,
		});
	}, [jqlFilters, jqlSimpleSearch, jqlContext, sortDirection]);

	const doFireErrorAnalytics = (error: ApolloError) => {
		fireErrorAnalytics({
			meta: {
				id: QUERY_NAME,
				packageName: 'jiraBusinessCalendar',
				teamName: 'wanjel',
			},
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			error: Array.isArray(error) && error.length > 0 ? error[0] : (error as ApolloError),
			attributes: { message: 'Failed to fetch unscheduled items in calendar' },
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	const { loading, error, data, loadFirstPage, isLoadingNextPage, loadNextPage, refetch } =
		useJqlPagination<
			JWMCalendarUnscheduledItems,
			ViewItemsNode,
			JWMCalendarUnscheduledItemsVariables
		>({
			variables: {
				cloudId,
				...variables,
				first: PAGE_SIZE,
			},
			query: QUERY,
			onError: (apolloError: ApolloError) => {
				if (data) {
					// we only want to show the flag if we had data to display at some point
					// if we were never able to get data we will show an error status
					showFlag({
						type: 'error',
						title: messages.errorTitle,
						description: messages.errorMessage,
					});
				}
				doFireErrorAnalytics(apolloError);
			},
			onCompleted: (responseData) => {
				fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} succeeded`, {
					issueCount: getAGGCompliantData<JWMCalendarUnscheduledItems>(responseData, 'list')
						?.totalCount,
					sortDirection,
					isSearching: !!jqlSimpleSearch,
				});
			},
			onPaginationError: doFireErrorAnalytics,
			fetchPolicy: 'cache-and-network',
		});

	useEffect(() => {
		loadFirstPage(); // fetch issues once on mount

		setIsInitialised(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const _data = getAGGCompliantData<JWMCalendarUnscheduledItems>(data, 'list');

	const allUnscheduledItems: UnscheduledItem[] | null = useMemo(
		() =>
			transformData<ViewItemsEdges, CalendarDataSourceItem<CalendarIssue>>({
				// @ts-expect-error - TS2739 - Type 'readonly (ListConnectionEdge<object> | null)[]' is missing the following properties from type 'JWMCalendarUnscheduledItems_jira_list_JiraWorkManagementViewItemConnection_edges': __typename, nodets
				edges: _data?.edges || null,
				areUnscheduledItems: true,
			}),
		[_data],
	);

	const fetchNextPage = useCallback(() => {
		fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} loadNextPage`, {
			issueCount: _data?.totalCount,
		});
		loadNextPage();
	}, [createAnalyticsEvent, _data?.totalCount, loadNextPage]);

	return {
		loading: loading || !isInitialised,
		error,
		data: allUnscheduledItems,
		isLoadingNextPage,
		hasNextPage: _data?.pageInfo?.hasNextPage ?? false,
		loadNextPage: fetchNextPage,
		refetch,
	};
};
